import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, firstValueFrom, of } from 'rxjs';
import { EmpresaComContexto } from '../models/empresa-com-contexto';
import { UserApiService } from './api/user-api.service';
import { AuthModuleService } from './auth-module.service';
import { CompanySelectionCookieService } from './company-selection-cookie.service';

/**
 * Cache central do multi-tenant. **Uma chamada, um cache**: no login o app
 * dispara `GET /user/me/empresas` uma única vez e todo o resto deriva daqui —
 * tela de seleção, `companyId` do tenant, agente comprador do chat de IA e a
 * flag de integração ERP do kanban.
 *
 * O estado é um `signal<EmpresaComContexto[] | null>`:
 * - `null` — ainda não carregado nesta sessão (bootstrap ou logo após um
 *   logout). Nenhum derivado decide nada: `empresaAtual()` fica `null`,
 *   `companyId()` fica `null`.
 * - `[]` — carregado, o usuário não tem empresa.
 * - lista — carregado.
 *
 * Por que signal e não um pipeline com `shareReplay(1)`: na troca de usuário na
 * mesma aba o `shareReplay` continuava servindo a lista do usuário anterior até
 * o novo `getMyCompanies()` resolver. O `companySelectionGuard` chegava a gravar
 * o cookie de empresa com um id que não era do usuário logado, e o dashboard
 * carregava dados da empresa errada. Com o signal, `resetSession()` põe `null`
 * na hora e ninguém enxerga tenant obsoleto.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly _auth = inject(AuthModuleService);
  private readonly _userApi = inject(UserApiService);
  private readonly _cookie = inject(CompanySelectionCookieService);
  private readonly _destroyRef = inject(DestroyRef);

  /** `null` = não carregado nesta sessão. Ver doc da classe. */
  private readonly _empresas = signal<EmpresaComContexto[] | null>(null);

  /**
   * Empresa (tenant) escolhida. Fonte de verdade reativa; o cookie
   * `selected_company_id` é só o espelho para SSR / reload. `escolherEmpresa()`
   * atualiza os dois — sem isto, trocar de empresa não recalculava
   * `empresaAtual()` / `companyId()` (cookie não é reativo).
   */
  private readonly _empresaSelecionadaId = signal<number | null>(this._cookie.getId());

  /** Guarda contra `carregar()` concorrente (constructor + `refetch`). */
  private _carregando = false;

  /** Lista já resolvida; `[]` enquanto não carregou. */
  readonly empresas = computed(() => this._empresas() ?? []);

  /**
   * `true` quando a lista já foi resolvida para a sessão atual (mesmo que
   * vazia). A página de seleção usa para distinguir "carregando" de
   * "0 empresas".
   */
  readonly carregado = computed(() => this._empresas() !== null);

  /**
   * `empresas` como Observable, **já filtrado**: só emite quando a lista foi
   * resolvida para a sessão atual (`!== null`). O `companySelectionGuard` faz
   * `firstValueFrom` disto — assim ele bloqueia até a lista real do usuário
   * logado, nunca decide com dado de sessão anterior.
   */
  readonly empresas$ = toObservable(this._empresas).pipe(
    filter((lista): lista is EmpresaComContexto[] => lista !== null)
  );

  /** A empresa (tenant) atual: a escolhida, senão a primeira da lista, senão null. */
  readonly empresaAtual = computed<EmpresaComContexto | null>(() => {
    const lista = this._empresas();
    if (lista === null) return null;
    const escolhidaId = this._empresaSelecionadaId();
    return lista.find(e => e.id === escolhidaId) ?? lista[0] ?? null;
  });

  readonly companyId = computed(() => this.empresaAtual()?.id ?? null);

  readonly precisaEscolher = computed(
    () => this.empresas().length > 1 && this._empresaSelecionadaId() === null
  );

  /** Agente comprador da empresa atual, ou null. */
  readonly agenteComprador = computed(
    () => this.empresaAtual()?.agentesExclusivos.find(a => a.tipo === 'comprador') ?? null
  );

  /** Integração ERP ativa na empresa atual. */
  readonly integracaoErpAtiva = computed(() => this.empresaAtual()?.integracaoErpAtiva ?? false);

  constructor() {
    // Logout / troca de usuário na mesma aba: zera a lista na hora — o próximo
    // `carregar()` (disparado pela reautenticação abaixo) traz a do novo
    // usuário. Ver docs/specs/limpeza-de-sessao-no-logout.md.
    this._auth.sessionCleared$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.resetSession());

    // Toda vez que a sessão passa a autenticada (bootstrap ou login), busca as
    // empresas do usuário.
    this._auth.isAuthenticatedChange$
      .pipe(
        filter(autenticado => autenticado),
        takeUntilDestroyed()
      )
      .subscribe(() => this.carregar());
  }

  /**
   * Busca `GET /user/me/empresas` e preenche o cache. Idempotente enquanto uma
   * busca está em andamento.
   */
  carregar(): void {
    if (this._carregando) return;
    this._carregando = true;
    this._userApi
      .getMyCompanies()
      .pipe(
        catchError(() => of([] as EmpresaComContexto[])),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(lista => {
        this._empresas.set(lista);
        this._carregando = false;
      });
  }

  /**
   * Escolhe a empresa (tenant). Atualiza o signal — `empresaAtual()`,
   * `companyId()`, `agenteComprador()`, `integracaoErpAtiva()` recalculam na
   * hora — e espelha para o cookie (SSR / reload). A tela de seleção navega
   * para `/main` em seguida só para sair da tela.
   */
  escolherEmpresa(empresaId: number): void {
    this._cookie.setId(empresaId);
    this._empresaSelecionadaId.set(empresaId);
  }

  /** Id da empresa escolhida (cookie/signal), ou `null`. */
  get empresaSelecionadaId(): number | null {
    return this._empresaSelecionadaId();
  }

  /** Limpa a escolha (cookie + signal) — ex.: cookie aponta para empresa de outro usuário. */
  limparEmpresaSelecionada(): void {
    this._cookie.clear();
    this._empresaSelecionadaId.set(null);
  }

  /**
   * Zera o cache. `empresaAtual()` / `companyId()` voltam a `null` na hora —
   * nenhum consumidor enxerga o tenant do usuário anterior.
   */
  resetSession(): void {
    this._empresas.set(null);
    this._empresaSelecionadaId.set(null);
    this._carregando = false;
  }

  /** Força re-fetch (ex.: após cadastrar/entrar numa nova empresa). */
  refetch(): void {
    this._empresas.set(null);
    this._carregando = false;
    this.carregar();
  }

  /** Aguarda a lista resolver para a sessão atual (usado por guards). */
  aguardarCarregado(): Promise<EmpresaComContexto[]> {
    return firstValueFrom(this.empresas$);
  }
}
